import type { CondoCardProps } from '../CondoCard';
import type { PropertyCardProps } from '../PropertyCard';
import type { CarouselGenericActionVariants } from '../ui/carousel/CarouselGenericAction';
import type { CarouselItemSelectorProps } from '../ui/carousel/CarouselItemSelector';
import type { CarouselProps } from '../ui/carousel/interface';
import type { ListingTrackingContext } from '~/composables/useListingEventTracking';
import type { Property } from '~/core/entities/property';
import type {
	CarouselNavigationDirection,
	SectionName
} from '~/types/mixpanel';
import type { ClassValue } from 'clsx';

import { cva } from 'class-variance-authority';

const size = {
	default: 'basis-[90%] sm:basis-[49%] xl:basis-[32.3%]',
	half: 'basis-[90%] sm:basis-[49%]',
	'half-responsive':
		'basis-[90%] sm:basis-[49%] md:basis-[48%] lg:basis-full xl:basis-[49%]',
	'half-seamless': 'basis-full lg:basis-[49.85%] 2xl:basis-[49.86%]', // 49.86% is the basis so that theres no whiteline in the end
	'sm-half': 'basis-[90%] sm:basis-[47%] md:basis-[47%]',
	third: 'basis-[90%] sm:basis-1/3',
	quarter: 'basis-[90%] sm:basis-[49%] lg:basis-[32%] xl:basis-[24%]',
	full: 'basis-full'
} as const;

export const carouselItemVariant = cva('min-h-fit', {
	variants: {
		size
	},
	defaultVariants: {
		size: 'default'
	}
});

/**
 * Props interface for StandardSlider component
 * @template T - Type of items to be displayed in the slider, defaults to Property
 */
export interface StandardSliderProps<T = Property> {
	items?: T[];
	containerClass?: ClassValue;
	mainContainerClass?: ClassValue;
	footerClass?: ClassValue;
	buttonVariant?: 'primary' | 'secondary' | 'tertiary';
	buttonClass?: ClassValue;
	buttonSkeletonClass?: ClassValue;
	sectionName?: SectionName;
	isLoading?: boolean;
	title?: string;
	viewAllHref?: string;
	viewAllText?: string;
	carouselItemClass?: ClassValue;
	carouselItemSize?: keyof typeof size;
	carouselOptions?: {
		cyclic?: boolean;
		align?: 'start' | 'center' | 'end';
		class?: ClassValue;
		carouselWrapperClasses?: ClassValue;
	};
	propertyCardVariant?:
		| PropertyCardProps['variant']
		| CondoCardProps['variant'];
	slidesToScroll?: number;
	breakpoints?: NonNullable<CarouselProps['opts']>['breakpoints'];
	titleAs?: string;
	titleClasses?: ClassValue;
	carouselContentClass?: ClassValue;
	carouselViewportClass?: ClassValue;
	showNavigationArrows?: boolean;
	alwaysShowArrows?: boolean;
	navigationArrowClass?: ClassValue;
	displayTitle?: boolean;
	displayFooter?: boolean;
	limitPhotos?: boolean;
	selectorVariant?: CarouselItemSelectorProps['variant'];
	selectorArrowsVariant?: CarouselGenericActionVariants['variant'];
	trackingContext?: ListingTrackingContext;
	/**
	 * Slide exibido quando os itens chegam. Serve para devolver o visitante ao
	 * ponto em que ele estava depois de voltar de outra página; sem isso o
	 * carrossel sempre reinicia no primeiro item.
	 */
	initialIndex?: number;
}

export interface StandardSliderNavigation {
	direction: CarouselNavigationDirection;
	fromIndex: number;
	toIndex: number;
}

export interface StandardSliderEmits {
	'cta-view-more-clicked': [
		payload: {
			destination_url: string;
			cta_text: string;
		}
	];

	navigate: [navigation: StandardSliderNavigation];
	'item-click': [payload: { item: Property; index: number }];
}

export { default as StandardSlider } from './Slider.vue';
export {
	StandardSliderArrowNext,
	StandardSliderArrowPrev
} from './StandardSliderArrows';
